import { Component, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import {
  FORM_FIELD_TYPES,
  FormField,
  FormFieldType,
  LocalizedText,
  createField,
  suggestFieldKey
} from '../../../shared/dynamic-form/form-schema.models';
import { FormBuilderEditCultureService } from './form-builder-edit-culture.service';
import { FormBuilderLocalizedInputComponent } from './form-builder-localized-input.component';

@Component({
  standalone: true,
  selector: 'ain-form-builder-repeatable-panel',
  imports: [
    FormsModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    AinFormFieldComponent,
    InfoHintComponent,
    FormBuilderLocalizedInputComponent
  ],
  template: `
    <section class="logic-panel">
      <ain-info-hint
        [titleKey]="'dynamic.builder.repeatable.infoTitle'"
        [descriptionKey]="'dynamic.builder.repeatable.infoDescription'"
        [bulletKeys]="['dynamic.builder.repeatable.infoBullet']" />

      <ain-form-field [label]="t('dynamic.builder.repeatable.minRows')">
        <p-inputNumber
          [ngModel]="field().repeatable?.minRows ?? 0"
          [disabled]="!canEdit()"
          [min]="0"
          [useGrouping]="false"
          (ngModelChange)="updateMinRows($event)" />
      </ain-form-field>

      <ain-form-field [label]="t('dynamic.builder.repeatable.maxRows')">
        <p-inputNumber
          [ngModel]="field().repeatable?.maxRows ?? 10"
          [disabled]="!canEdit()"
          [min]="1"
          [useGrouping]="false"
          (ngModelChange)="updateMaxRows($event)" />
      </ain-form-field>

      <div class="header-row">
        <strong>{{ t('dynamic.builder.repeatable.itemFields') }}</strong>
        @if (canEdit()) {
          <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.repeatable.addItemField')" (click)="addItemField()"></button>
        }
      </div>

      @for (itemField of field().repeatable?.itemFields ?? []; track itemField.key; let idx = $index) {
        <article class="item-card">
          <ain-form-builder-localized-input
            [label]="t('dynamic.builder.repeatable.itemLabel')"
            [text]="itemField.label"
            [disabled]="!canEdit()"
            (textChange)="updateItemLabel(idx, $event)" />

          <ain-form-field [label]="t('dynamic.builder.repeatable.itemKey')">
            <input pInputText [ngModel]="itemField.key" [disabled]="!canEdit()" (ngModelChange)="updateItemKey(idx, $event)" />
          </ain-form-field>

          <ain-form-field [label]="t('dynamic.builder.repeatable.itemType')">
            <p-select
              [options]="itemTypeOptions()"
              optionLabel="label"
              optionValue="value"
              [ngModel]="itemField.type"
              [disabled]="!canEdit()"
              appendTo="body"
              (ngModelChange)="updateItemType(idx, $event)" />
          </ain-form-field>

          <div class="checkbox-row">
            <p-checkbox
              binary
              [ngModel]="!!itemField.required"
              [disabled]="!canEdit()"
              (ngModelChange)="updateItemRequired(idx, $event)" />
            <label>{{ t('dynamic.builder.properties.required') }}</label>
          </div>

          @if (canEdit()) {
            <button pButton type="button" severity="danger" text icon="pi pi-trash" (click)="removeItemField(idx)"></button>
          }
        </article>
      }
    </section>
  `,
  styles: [`
    .logic-panel { display: grid; gap: 12px; }
    .header-row { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .item-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px; display: grid; gap: 8px; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
  `]
})
export class FormBuilderRepeatablePanelComponent {
  private readonly localization = inject(LocalizationService);
  private readonly cultureService = inject(FormBuilderEditCultureService);

  readonly field = input.required<FormField>();
  readonly canEdit = input(false);
  readonly fieldChange = output<FormField>();

  itemTypeOptions() {
    return FORM_FIELD_TYPES
      .filter(item => !item.isLayoutBreak && !['repeatable', 'matrix', 'computed', 'content', 'lookup'].includes(item.value))
      .map(item => ({ label: this.t(item.labelKey), value: item.value }));
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private emit(next: FormField): void {
    this.fieldChange.emit(next);
  }

  private repeatable() {
    return this.field().repeatable ?? { itemFields: [] };
  }

  updateMinRows(value: number | null): void {
    this.emit({ ...this.field(), repeatable: { ...this.repeatable(), minRows: value ?? 0, itemFields: this.repeatable().itemFields ?? [] } });
  }

  updateMaxRows(value: number | null): void {
    this.emit({ ...this.field(), repeatable: { ...this.repeatable(), maxRows: value ?? 10, itemFields: this.repeatable().itemFields ?? [] } });
  }

  addItemField(): void {
    const culture = this.cultureService.editCulture();
    const keys = new Set((this.repeatable().itemFields ?? []).map(item => item.key));
    const created = createField('text', culture, keys, 'Item');
    this.emit({
      ...this.field(),
      repeatable: {
        ...this.repeatable(),
        itemFields: [...(this.repeatable().itemFields ?? []), created]
      }
    });
  }

  removeItemField(index: number): void {
    const itemFields = [...(this.repeatable().itemFields ?? [])];
    itemFields.splice(index, 1);
    this.emit({ ...this.field(), repeatable: { ...this.repeatable(), itemFields } });
  }

  updateItemLabel(index: number, label: LocalizedText): void {
    const itemFields = [...(this.repeatable().itemFields ?? [])];
    itemFields[index] = { ...itemFields[index], label };
    this.emit({ ...this.field(), repeatable: { ...this.repeatable(), itemFields } });
  }

  updateItemKey(index: number, key: string): void {
    const itemFields = [...(this.repeatable().itemFields ?? [])];
    const existing = new Set(itemFields.map(item => item.key));
    existing.delete(itemFields[index].key);
    itemFields[index] = { ...itemFields[index], key: suggestFieldKey(key, existing) };
    this.emit({ ...this.field(), repeatable: { ...this.repeatable(), itemFields } });
  }

  updateItemType(index: number, type: FormFieldType): void {
    const itemFields = [...(this.repeatable().itemFields ?? [])];
    itemFields[index] = { ...itemFields[index], type };
    this.emit({ ...this.field(), repeatable: { ...this.repeatable(), itemFields } });
  }

  updateItemRequired(index: number, required: boolean): void {
    const itemFields = [...(this.repeatable().itemFields ?? [])];
    itemFields[index] = { ...itemFields[index], required };
    this.emit({ ...this.field(), repeatable: { ...this.repeatable(), itemFields } });
  }
}
